import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { findPathByUuId } from '../../../utils/UuIdGenerator';
import { change } from '../utils/actions';
import DeprecatedConfirmationPrompt, { FluentDefaultConfirmationComponent } from '../../modals/ConfirmationPrompt';
import { IActionType, IConfirmationTriggeringAction } from './usePerformSave';
import setIn from '../utils/setIn';
import FluentModal from '../../modals/FluentModal';
import { DocumentNode, MutationFunctionOptions, MutationResult, useMutation } from '@apollo/client';
import { useP4ToastController } from '../../../views/system/NotificationsArea';
import { AlertType } from '../../../graphql/schema/p4types';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import type { MutationHookOptions } from '@apollo/client/react/types/types';

export type IConfirmationState = {
  action: IActionType;
  meta: any;
  title: string;
  message: string;
  submittedValues: any; //Values submitted may be a custom override
};

type UseConfirmationPromptType = (
  /**
   * A function that takes an action object to trigger some logic (Expected to be a P4 form save).
   */
  actionFunction: IConfirmationTriggeringAction,
  /**
   * State object provided by the custom alert handler call from save.
   */
  confirmationState: IConfirmationState | null,
  /**
   * Setter for state - Used to clear it down once the confirmation prompt has been responded to.
   */
  setConfirmationState: React.Dispatch<React.SetStateAction<IConfirmationState | null>>
) => ReactNode;

/**
 * Gets a confirmation prompt element that can be used to re-submit a save action when a confirmation option is selected.
 * Supports multi-response confirmation prompts.
 * @returns A multi-option confirmation prompt.
 */
export const useConfirmationPrompt: UseConfirmationPromptType = (
  actionFunction,
  confirmationState,
  setConfirmationState
) => {
  const [resubmitAction, setResubmitAction] = useState<IActionType | null>(null);
  const [resubmitActionValues, setResubmitActionValues] = useState(null);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  //When resubmitAction is set, trigger a save.
  //Moved to a useEffect like this because we need connectedForm to have a chance to update its state from context.
  useEffect(() => {
    if (resubmitAction) {
      actionFunction(resubmitAction, resubmitActionValues);
      setResubmitAction(null);
    }
    // Action function not expected to change - Explicitly only want this to trigger when resubmitAction or resubmitActionValues change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resubmitAction, resubmitActionValues]);

  if (!confirmationState) return null;

  const responseCount = confirmationState.meta.responses?.length || 2;

  //Multi-Option Response Handling.
  const confirmPromptResponse = (value: any) => {
    let field = `confirmationResponses.${[confirmationState.meta.confirmationReference]}`;
    field = findPathByUuId(connectedForm.values, confirmationState.meta.uuid) + field;
    if (confirmationState.submittedValues) {
      setResubmitActionValues(setIn(confirmationState.submittedValues, field, value));
      setResubmitAction(confirmationState.action);
    } else {
      Promise.resolve(dispatch(change(connectedForm.entity?.type, field, value, false))).then(() => {
        setResubmitAction(confirmationState.action);
      });
    }
    setConfirmationState(null);
  };

  return (
    <FluentModal isOpen={true} styles={{ main: { width: `${350 + responseCount * 100}px`, minHeight: '250px' } }}>
      <div style={{ padding: '1em' }}>
        <DeprecatedConfirmationPrompt
          onReject={() => setConfirmationState(null)}
          title="Confirmation Required"
          confirm={() => confirmPromptResponse(true)}
          message={confirmationState.message}
          confirmationResponses={confirmationState.meta.responses}
          onConfirmPromptResponse={confirmPromptResponse}
        />
      </div>
    </FluentModal>
  );
};

/**
 * A wrapper around 'useMutation' to add confirmation prompt handling to it.
 */
export const useConfirmationMutation = <TResult, TVariables>(
  mutation: DocumentNode,
  mutationOptions: MutationHookOptions<TResult, TVariables>,
  additionalOptions: { confirmationPath: string; entity?: EntityType }
): [
  runMutation: (options?: MutationFunctionOptions<TResult, TVariables>) => void,
  result: MutationResult<TResult> & { confirmationPrompt: ReactNode }
] => {
  const { dispatchSimpleToast } = useP4ToastController();
  const [options, setOptions] = useState<any>();
  const { confirmationPath, entity } = additionalOptions;

  const [confirmationState, setConfirmationState] = useState<{
    meta: any;
    title: string;
    message: string;
  } | null>(null);
  const handleQueryError = useComponentQueryErrorHandling({
    name: 'Trade Counter',
    customAlertHandler: (_: unknown, meta: any, title: string, message: string) => {
      if (meta.confirmationReference) {
        setConfirmationState({
          meta,
          title,
          message,
        });
        return;
      }
      //If no custom handling, use basic handling (Display a toast)
      dispatchSimpleToast(title, message, AlertType.Warning);
    },
    entity,
  });

  const { onError, ...otherMutationOptions } = mutationOptions;
  const onErrorWrapper = useCallback(
    (error: Parameters<typeof onError>) => {
      handleQueryError(error);
      onError?.(error);
    },
    [handleQueryError, onError]
  );

  const [runMutation, mutationMeta] = useMutation<TResult, TVariables>(mutation, {
    ...otherMutationOptions,
    onError: onErrorWrapper,
  });

  const runMutationWrapper = useCallback<typeof runMutation>(
    (options) => {
      setOptions(options);
      // Explicitly void the promise to encourage "onCompleted" callback to be used instead.
      // This is because the confirmation prompt layer would not call the original promise callback,
      //   which makes it likely to lead to bugs. (Expected follow-up behaviour would be missed any time a confirmation prompt was triggerred).
      return void runMutation(options);
    },
    [runMutation]
  );

  const responseCount = confirmationState?.meta.responses?.length || 2;

  const confirmPromptResponse = useCallback(
    (value: any) => {
      const field = `confirmationResponses.${[confirmationState?.meta.confirmationReference]}`;
      const newOptions = setIn(options, `${confirmationPath}.${field}`, value);
      // eslint-disable-next-line no-void
      void runMutationWrapper(newOptions);
    },
    [confirmationPath, confirmationState?.meta.confirmationReference, options, runMutationWrapper]
  );

  return useMemo(() => {
    const confirmationPrompt = (
      <FluentModal isOpen={!!confirmationState}>
        <div style={{ width: `${350 + responseCount * 100}px`, minHeight: '250px', position: 'relative' }}>
          <DeprecatedConfirmationPrompt
            onReject={() => setConfirmationState(null)}
            title="Confirmation Required"
            confirm={() => confirmPromptResponse(true)}
            message={confirmationState?.message}
            confirmationResponses={confirmationState?.meta.responses}
            onConfirmPromptResponse={confirmPromptResponse}
            component={FluentDefaultConfirmationComponent}
          />
        </div>
      </FluentModal>
    );
    return [runMutationWrapper, { ...mutationMeta, confirmationPrompt }];
  }, [confirmPromptResponse, confirmationState, mutationMeta, responseCount, runMutationWrapper]);
};
